import {v3} from "oc-threejs/short3";
import Interieur from "../arrangement/Interieur";
import Element from "../arrangement/Element";
import PriorityIntervals from "./PriorityIntervals";
import Space from "./Space";
import Fit from "./Fit";

/**
 * Misst den freien Raum und die Positionierungsmöglichkeit eines Interieurs in einem Element
 */
export default class Measurer {

    // beliebig belegbarer Raum
    static FREE = 0;
    // Raum, der frei bleiben muss, aber kollabieren kann (z.B. Mindest-Fachhöhe)
    static COLLAPSE = 1;
    // Raum, der frei bleiben muss
    static SPACE = 2;
    // belegter Platz, den kein anderer Occupied einnehmen kann
    static OCCUPIED = 3;

    // Positionen für Measurer und DragHandle
    static BOTTOM = Symbol('BOTTOM');
    static CENTER = Symbol('CENTER');
    static TOP = Symbol('TOP');

    mode: Symbol;

    constructor(mode = Measurer.BOTTOM) {
        this.mode = mode;
    }

    /**
     * Drag-Bereich für einen HeightHandle (verschieben der oberen/unteren Kante)
     */
    getHeightDragHandleYFits(owner, border: Symbol): Fit[] {

        // ermittle alle Bereiche, in denen ein Konstruktionsboden sein kann
        // dieser Fit ist nur durch innenliegendes Interieur / Corpus begrenzt
        const elementIntervals = this._getElementIntervalsForInterieur(owner.element, owner, i => i===owner);

        // ermittle den Fit, in dem die Tür liegt, um den maximalen Platz für die Tür zu haben
        // dieser Fit ist nur durch Fronten / Corpus begrenzt
        const tuerYFits = this.getFullHeightYFits(owner, owner.element);
        const tuerCenterY = owner.schrankBox.getCenter().y;
        const tuerFit = tuerYFits.find(fit => fit.min <= tuerCenterY && tuerCenterY <= fit.max);
        if (!tuerFit) {
            // offenbar ein illegaler Zustand der Tür..
            return [];
        }

        // erlaube nur die Intervalle innerhalb des TuerFits
        elementIntervals.add(Number.NEGATIVE_INFINITY, tuerFit.minCollapse, Number.POSITIVE_INFINITY);
        elementIntervals.add(tuerFit.maxCollapse, Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY);

        // erlaube nur die Intervalle bis zur Mindesthöhe der eigenen Tür
        if (border === Measurer.BOTTOM) {
            elementIntervals.add(owner.schrankBox.max.y - owner.element.schrank.options.getFloat('tuer_hoehe'), Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY);
        } else {
            elementIntervals.add(Number.NEGATIVE_INFINITY, owner.schrankBox.min.y + owner.element.schrank.options.getFloat('tuer_hoehe'), Number.POSITIVE_INFINITY);
        }

        elementIntervals.partition().finite();
        // JVI: Giving up here - nochmal mit SBA besprechen.
        // IP ist das die Lösung, führt aber zu komischen Effekten.
        // elementIntervals.add(tuerFit.minCollapse, tuerFit.minCollapse, 0);
        // elementIntervals.add(tuerFit.maxCollapse, tuerFit.maxCollapse, 0);

        // mappe die Intervalle auf Fits
        const fits = [];
        elementIntervals.list.forEach((ei, ei_index) => {
            if (ei[2] === Measurer.FREE) {
                const freeSpace = new Space().setFromFree(owner.element, elementIntervals, ei_index);
                const fit = freeSpace.getFit(owner, true, false);
                if (fit) {
                    fits.push(fit);
                }
            }
        });

        return fits;
    }

    /**
     * Drag-Bereich für eine Tür, wenn sie in der Mitte angefasst wird
     */
    getFullHeightYFits(owner, element: Element): Fit[] {

        const elementIntervals = this._getElementIntervalsForTuer(element, owner);

        const fits = [];
        elementIntervals.list.forEach((ei, ei_index) => {
            if (ei[2] === Measurer.FREE) {
                const freeSpace = new Space().setFromFree(element, elementIntervals, ei_index);
                const fit = freeSpace.getMaxFit();
                if (element.frontSloped && !owner.canTopBeSlopedInElement(element)) {
                    if (fit.maxCollapse > element.schrankSlopeTop) {
                        const brett_staerke = element.schrank.options.getFloat('brett_staerke');
                        fit.max = fit.maxCollapse = element.schrankSlopeTop - brett_staerke;
                    }
                }

                if ((fit.maxCollapse - fit.minCollapse) > element.schrank.options.getFloat('tuer_hoehe')) {
                    fits.push(fit);
                }

            }
        });
        return fits;
    }

    /**
     * Drag-Bereiche für ein beliebiges Interieur
     */
    getInterieurYFits(interieurToFit: Interieur, element: Element, setInterieurToFitSizeZero = false): Fit[] {

        const elementIntervals = this._getElementIntervalsForInterieur(element, interieurToFit);
        const fits = [];
        elementIntervals.list.forEach((ei, ei_index) => {
            if (ei[2] === Measurer.FREE) {
                const freeSpace = new Space().setFromFree(element, elementIntervals, ei_index);
                const fit = freeSpace.getFit(interieurToFit, setInterieurToFitSizeZero, this.mode === Measurer.CENTER);
                if (fit) {
                    fits.push(fit);
                }
            }
        });
        return fits;
    }

    /**
     * Einzelner Bereich, in dem ein Interieur liegt
     */
    getSurroundingInterieurYFit(interieur: Interieur): ?Fit {
        const fits = this.getInterieurYFits(interieur, interieur.element);
        const centerY = interieur.schrankBox.getCenter().y;
        return fits.find(fit => fit.min <= centerY && centerY <= fit.max);
    }

    _getElementIntervalsForTuer(element, interieurToFit) {
        const intervals = new PriorityIntervals();
        this._setFreeIntervallAndForbidEverythingOutside(intervals,element.schrankBox.min.y, element.schrankBox.max.y);

        const otherInerieur = element.interieurs.filter(interieur => !interieurToFit.canOverlay(interieur));
        otherInerieur.forEach(interieur => this._addInteriorIntervals(intervals, interieur));

        const konstruktionsboeden = element.konstruktionsboeden
            .filter(boden =>
                otherInerieur.some(intereur => intereur.hasBoden(boden)));

        const otherBoeden = [element.schrank.schrankboden]
            .concat(element.deckel.filter(b => b.isPlanar()))
            .concat(konstruktionsboeden)
            .filter(boden => !interieurToFit.ownsBoden(boden));

        otherBoeden.forEach(boden => this._addBodenIntervals(intervals, element, boden));

        return intervals.partition().finite();
    }

    _getElementIntervalsForInterieur(element, interieurToFit, canOverlay=i=>interieurToFit.canOverlay(i)) {
        const intervals = new PriorityIntervals();

        // Problem im fall angleSide === 'back' / slope: das Free-Intervall ist nicht erweiterbar
        let maxTopPlain = element.schrank.angleSide === 'back'
            ? element.getMaxHeightForMinDepth(interieurToFit)
            : element.slopeTop;
        if(element.schrank.sockelOption == "mitBlende") {
            maxTopPlain += Math.max(0, element.schrank.sockelHoehe);
        }
        let maxTop = Math.min(element.schrankBox.max.y, element.parent.localToSchrank(v3(0,maxTopPlain,0)).y);

        const epsilon = 0.0001;
        if (interieurToFit.needsBoden.top && Math.abs(maxTop-element.schrankSlopeTop)<epsilon && element.schrankSlopeTop+epsilon<element.schrankBox.max.y) {
            // für den Fall, dass die obere Begrenzung ein Dachschrägen-Knick ist, und das Interieur einen Top-Boden hat,
            // darf der Konstruktionsboden nicht in den Knick hinein reichen
            const brett_staerke = element.schrank.options.getFloat('brett_staerke');
            maxTop -= brett_staerke;
        }

        maxTop = Math.max(element.schrankBox.min.y,maxTop);

        this._setFreeIntervallAndForbidEverythingOutside(intervals,element.schrankBox.min.y, maxTop);

        // Wenn für ein DragHandle Elemente gesucht werden, kann die Tür, zb die Schublade eben NICHT overlayen.
        const otherInterieur = element.interieurs.filter(interieur => !canOverlay(interieur));
        otherInterieur.forEach(interieur => this._addInteriorIntervals(intervals, interieur));
        const otherBoeden = [element.parent.schrankboden]
            .concat(element.deckel.filter(b=>b.isPlanar()))
            .concat(element.konstruktionsboeden.filter(boden => !interieurToFit.ownsBoden(boden)))
            .filter(boden => boden);

        otherBoeden.forEach(boden => this._addBodenIntervals(intervals, element, boden));
        return intervals.partition().finite();
    }

    _addInteriorIntervals(intervals, interieur) {
        const min = interieur.schrankBox.min.y;
        const max = interieur.schrankBox.max.y;
        intervals.add(min - interieur.space.bottom, max + interieur.space.top, Measurer.SPACE);
        intervals.add(min, max, Measurer.OCCUPIED);
    }

    _addBodenIntervals(intervals, element, boden) {
        const minY = boden.schrankBox.min.y;
        const maxY = boden.schrankBox.max.y;
        const fach_hoehe = element.schrank.options.getFloat('fach_hoehe');
        const brett_staerke = element.schrank.options.getFloat('brett_staerke');
        // das folgende habe ich mit einem Dummy-Interieur ausgetestet, das seine Höhe nicht verändert
        // (Krawattenauszug auf needBodent top/bottom true gesetzt und space top/bottom auf 0)
        // die Fachmindesthöhe wird dann während des Drags richtig eingehalten
        const h = fach_hoehe + brett_staerke;
        intervals.add(minY - h, maxY + h, Measurer.COLLAPSE);
        intervals.add(minY, maxY, Measurer.OCCUPIED);
    }

    _setFreeIntervallAndForbidEverythingOutside(intervals,from,to) {
        intervals.add(Number.NEGATIVE_INFINITY, from, Number.POSITIVE_INFINITY);
        intervals.add(from, to, Measurer.FREE);
        intervals.add(to, Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY);
    }

}

